import type { Block, Point, Rect } from './structure-layout.types';

// The box every node is laid out in, and where its outline meets the lines attached to it.
export interface NodeFrame {
  width: number;
  height: number;
  // Between nodes stacked in a column.
  rowGap: number;
  // Lines leave the bottom this far in from the left edge.
  trunkInset: number;
  // How far inside the box's left or right edge the outline is at mid-height.
  sideInsetOf(entityId: number): number;
  // Lanes from several owners enter the top no closer than this to either side.
  topEntryInsetOf(entityId: number): number;
}

class LayoutGeometryHelper {
  static readonly NODE_WIDTH = 256;

  static readonly NODE_HEIGHT = 56;

  static readonly TRUNK_INSET = 28;

  static readonly MIN_INDENT = 12;

  static readonly SPLIT_MIN_INDENT = 28;

  static readonly ROW_GAP = 12;

  static readonly FIRST_ROW_GAP = 24;

  static readonly CHAIN_GAP = 40;

  static readonly BUS_GAP = 32;

  static readonly BUS_DROP = 32;

  static readonly PAIR_BUS_DROP = 32;

  static readonly PAIR_GAP = 64;

  static readonly GROUP_GAP = 64;

  static readonly GROUPS_PER_ROW = 4;

  static readonly ROOT_GAP = 96;

  static readonly ROOT_ROW_GAP = 96;

  static readonly SPLIT_AT = 8;

  static readonly MAX_ROWS = 10;

  static readonly CORNER_RADIUS = 12;

  static readonly LABEL_HEIGHT = 21;

  static readonly LABEL_GAP = 8;

  static readonly LABEL_LIFT = 6;

  static readonly LABEL_PADDING_X = 6;

  static readonly LABEL_CLEARANCE = 4;

  static readonly CHIP_HEIGHT = 21;

  static readonly CHIP_GAP = 4;

  static readonly CHIP_INSET = 12;

  static readonly CHIP_PADDING_X = 6;

  static readonly ENTRY_INSET = 24;

  static readonly CARD_FRAME: NodeFrame = {
    width: LayoutGeometryHelper.NODE_WIDTH,
    height: LayoutGeometryHelper.NODE_HEIGHT,
    rowGap: LayoutGeometryHelper.ROW_GAP,
    trunkInset: LayoutGeometryHelper.TRUNK_INSET,
    sideInsetOf: () => 0,
    topEntryInsetOf: () => LayoutGeometryHelper.ENTRY_INSET,
  };

  static translateBlock(block: Readonly<Block>, dx: number, dy: number): Block {
    const move = <T extends Point>(item: T): T => ({
      ...item,
      x: item.x + dx,
      y: item.y + dy,
    });
    return {
      ...block,
      nodes: block.nodes.map(move),
      lines: block.lines.map((line) => ({
        ...line,
        points: line.points.map(move),
      })),
      labels: block.labels.map(move),
      chips: block.chips.map(move),
    };
  }

  static unionRects(rects: ReadonlyArray<Rect>): Rect {
    if (rects.length === 0) return { x: 0, y: 0, width: 0, height: 0 };
    const left = Math.min(...rects.map((r) => r.x));
    const top = Math.min(...rects.map((r) => r.y));
    const right = Math.max(...rects.map((r) => r.x + r.width));
    const bottom = Math.max(...rects.map((r) => r.y + r.height));
    return { x: left, y: top, width: right - left, height: bottom - top };
  }

  static pointsToRect(points: ReadonlyArray<Point>): Rect {
    return LayoutGeometryHelper.unionRects(
      points.map((p) => ({ x: p.x, y: p.y, width: 0, height: 0 }))
    );
  }

  // Axis-aligned segments only. Touching an edge is not a crossing.
  static segmentCrossesRect(
    a: Readonly<Point>,
    b: Readonly<Point>,
    rect: Readonly<Rect>
  ): boolean {
    const left = Math.min(a.x, b.x);
    const right = Math.max(a.x, b.x);
    const top = Math.min(a.y, b.y);
    const bottom = Math.max(a.y, b.y);
    return (
      left < rect.x + rect.width &&
      right > rect.x &&
      top < rect.y + rect.height &&
      bottom > rect.y
    );
  }

  static rectsOverlap(a: Readonly<Rect>, b: Readonly<Rect>): boolean {
    return (
      a.x < b.x + b.width &&
      a.x + a.width > b.x &&
      a.y < b.y + b.height &&
      a.y + a.height > b.y
    );
  }

  static simplify(points: ReadonlyArray<Point>): Point[] {
    const deduped = points.filter(
      (p, i) => i === 0 || p.x !== points[i - 1].x || p.y !== points[i - 1].y
    );
    return deduped.filter((p, i) => {
      if (i === 0 || i === deduped.length - 1) return true;
      const prev = deduped[i - 1];
      const next = deduped[i + 1];
      return !(
        (prev.x === p.x && p.x === next.x) ||
        (prev.y === p.y && p.y === next.y)
      );
    });
  }

  static getPathD(points: ReadonlyArray<Point>): string {
    const path = LayoutGeometryHelper.simplify(points);
    if (path.length === 0) return '';
    const commands = [`M ${path[0].x} ${path[0].y}`];
    path.slice(1, -1).forEach((corner, i) => {
      const prev = path[i];
      const next = path[i + 2];
      const lengthIn =
        Math.abs(corner.x - prev.x) + Math.abs(corner.y - prev.y);
      const lengthOut =
        Math.abs(next.x - corner.x) + Math.abs(next.y - corner.y);
      const radius = Math.min(
        LayoutGeometryHelper.CORNER_RADIUS,
        lengthIn / 2,
        lengthOut / 2
      );
      const inX = Math.sign(corner.x - prev.x);
      const inY = Math.sign(corner.y - prev.y);
      const outX = Math.sign(next.x - corner.x);
      const outY = Math.sign(next.y - corner.y);
      commands.push(`L ${corner.x - inX * radius} ${corner.y - inY * radius}`);
      commands.push(
        `Q ${corner.x} ${corner.y} ${corner.x + outX * radius} ${corner.y + outY * radius}`
      );
    });
    const last = path[path.length - 1];
    if (path.length > 1) commands.push(`L ${last.x} ${last.y}`);
    return commands.join(' ');
  }
}

export { LayoutGeometryHelper };
